import type { PostMessageFunction } from '../message/types/PostMessageFunction';
import { getHeightWithMargins } from './getHeightWithMargins';
import { createMessage } from '../message/createMessage';
import debounce from 'lodash/debounce';
import { Logger } from '@thesavingsgroup/logger/Logger';
import { postMessage } from '@thesavingsgroup/components/message/postMessage';

type ObserverDependencies = {
  debounceTime?: number;
  global?: Window;
  iframeId: string;
  logger?: Logger;
  postHeightMessage?: PostMessageFunction;
};

type ObserverResult = () => void;

const DEFAULT_DEBOUNCE_TIME = 100;

export const observeResizeAndMutation =
  (
    {
      debounceTime = DEFAULT_DEBOUNCE_TIME,
      global,
      iframeId,
      logger = new Logger(),
      postHeightMessage = postMessage({ global, logger }),
    }: ObserverDependencies = { iframeId: 'unknown' },
  ) =>
  (element: Element): ObserverResult => {
    let lastHeight = getHeightWithMargins(element);
    postHeightMessage(createMessage({ height: lastHeight, iframeId }));

    const callback = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        logger.info('[COMPONENTS] Calculating height', {
          newHeight,
          lastHeight,
        });
        if (newHeight !== lastHeight) {
          lastHeight = newHeight;
          postHeightMessage(createMessage({ height: newHeight, iframeId }));
        }
      }
    };

    const resizeObserver = new ResizeObserver(debounce(callback, debounceTime));
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(() =>
      resizeObserver.observe(element),
    );

    mutationObserver.observe(element, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  };
